/**
 * AuditCascadeView — cadeia de auditoria por entityId (ADR-107), reusando o
 * `AuditTreeView` do design system (defense-in-depth via `hasSensitive`).
 *
 * Consome `GET /v1/audit-events/cascade/:entityId`. A raiz é o evento mais antigo
 * da entidade; os filhos ficam aninhados sob ela.
 */

import { AuditTreeView } from '@lume/ui-components';

import { buildCascadeNodes } from './audit-labels';
import { useAuditCascade } from './hooks/use-audit-events';

interface Props {
  entityId: string;
  /** false quando o caller não tem audit-events-read-sensitive → placeholder nos nós sensíveis. */
  canReadSensitive: boolean;
}

export function AuditCascadeView({ entityId, canReadSensitive }: Props): JSX.Element {
  const { data, isLoading, isError } = useAuditCascade(entityId);

  if (isLoading) {
    return <p className="text-xs text-text-secondary">Carregando cadeia…</p>;
  }
  if (isError || data === undefined) {
    return <p className="text-xs text-text-secondary">Cadeia indisponível para esta entidade.</p>;
  }

  const nodes = buildCascadeNodes(data.root, data.children);
  const total = data.children.length + 1;

  return (
    <div>
      <p className="mb-2 text-xs text-text-secondary">
        {total} evento{total === 1 ? '' : 's'} na cadeia desta entidade.
      </p>
      <AuditTreeView
        nodes={nodes}
        hasSensitive={!canReadSensitive}
        sensitivePlaceholder="Evento sensível — requer audit-events-read-sensitive"
        expandedIds={[data.root.id]}
        ariaLabel="Cadeia de auditoria da entidade"
      />
    </div>
  );
}
